import { SPEND_SCALE, fitCurve, parameterCount } from './fitCurve';

export const ROAS_HORIZON = 24;
export const MIN_TRAIN = 3;

export const DEV_FACTOR_MODE = {
  ALL_COHORTS: 'all_cohorts',
  PRIOR_ONLY: 'prior_only',
};

function finiteOrNull(value) {
  return Number.isFinite(value) ? value : null;
}

function roasAt(cohort, algorithmMonth) {
  return finiteOrNull(cohort.roas?.[algorithmMonth - 1]);
}

function sampleStd(values) {
  if (values.length < 2) {
    return null;
  }
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const sumOfSquares = values.reduce(
    (sum, value) => sum + (value - mean) * (value - mean),
    0
  );
  return Math.sqrt(sumOfSquares / (values.length - 1));
}

function developmentCurve(pool, horizon) {
  const dev = [1];
  for (let algorithmMonth = 1; algorithmMonth < horizon; algorithmMonth += 1) {
    let numerator = 0;
    let denominator = 0;
    let qualifyingCohorts = 0;
    pool.forEach((cohort) => {
      const current = roasAt(cohort, algorithmMonth);
      const next = roasAt(cohort, algorithmMonth + 1);
      if (current == null || next == null || !(cohort.uaSpend > 0)) {
        return;
      }
      numerator += cohort.uaSpend * next;
      denominator += cohort.uaSpend * current;
      qualifyingCohorts += 1;
    });
    const factor =
      qualifyingCohorts === 0 || denominator === 0 ? 1 : numerator / denominator;
    dev.push(dev[dev.length - 1] * factor);
  }
  return dev;
}

function predictM1(cohorts, index, fitType) {
  const training = cohorts.slice(0, index).filter((cohort) => {
    const m1 = roasAt(cohort, 1);
    return cohort.uaSpend > 0 && m1 != null && m1 > 0;
  });
  const minimumPoints = Math.max(MIN_TRAIN, parameterCount(fitType) + 1);
  if (training.length < minimumPoints || !(cohorts[index].uaSpend > 0)) {
    return null;
  }

  const fit = fitCurve(
    fitType,
    training.map((cohort) => ({
      x: cohort.uaSpend / SPEND_SCALE,
      y: roasAt(cohort, 1),
    }))
  );
  if (!fit) {
    return null;
  }

  const predictedM1 = fit.evaluate(cohorts[index].uaSpend / SPEND_SCALE);
  if (predictedM1 == null || !Number.isFinite(predictedM1)) {
    return null;
  }
  return { predictedM1, fit };
}

export function exanteBandBounds(exante, sigma, k) {
  if (!Number.isFinite(exante) || sigma == null || !Number.isFinite(sigma)) {
    return null;
  }
  return {
    lower: exante * (1 - k * sigma),
    upper: exante * (1 + k * sigma),
  };
}

function sigmaByMonth(results, horizon) {
  const sigma = [];
  let lastSigma = null;
  for (let algorithmMonth = 1; algorithmMonth <= horizon; algorithmMonth += 1) {
    const errors = [];
    results.forEach((result) => {
      const actual = roasAt(result, algorithmMonth);
      const exante = result.exante?.[algorithmMonth - 1];
      if (actual == null || exante == null || exante === 0) {
        return;
      }
      errors.push(actual / exante - 1);
    });
    if (errors.length >= 3) {
      lastSigma = sampleStd(errors);
    }
    sigma.push(lastSigma);
  }
  return sigma;
}

export function forecastRoas(cohorts, options = {}) {
  const fitType = options.fitType;
  const horizon = options.horizon || ROAS_HORIZON;
  const devFactorMode = options.devFactorMode || DEV_FACTOR_MODE.ALL_COHORTS;
  const bandK = options.bandK || [1, 2];
  const source = cohorts || [];
  const sharedDev =
    devFactorMode === DEV_FACTOR_MODE.ALL_COHORTS
      ? developmentCurve(source, horizon)
      : null;

  const results = source.map((cohort, index) => {
    const prediction = predictM1(source, index, fitType);
    const dev =
      sharedDev || developmentCurve(source.slice(0, index), horizon);
    if (!prediction) {
      return {
        ...cohort,
        predictedM1: null,
        exante: null,
        fit: null,
        dev,
        bands: null,
      };
    }

    const exante = dev.map((factor) => prediction.predictedM1 * factor);
    return {
      ...cohort,
      predictedM1: prediction.predictedM1,
      exante,
      fit: {
        fitType,
        coefficients: prediction.fit.coefficients,
        r2: prediction.fit.r2,
        n: prediction.fit.n,
      },
      dev,
      bands: null,
    };
  });

  const sigma = sigmaByMonth(results, horizon);
  results.forEach((result) => {
    if (!result.exante) {
      return;
    }
    const bands = {};
    bandK.forEach((k) => {
      bands[k] = result.exante.map((value, index) =>
        exanteBandBounds(value, sigma[index], k)
      );
    });
    result.bands = bands;
  });

  return {
    cohorts: results,
    dev: sharedDev,
    sigma,
    fitType,
  };
}
